import React, { useEffect, useContext } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Oval } from 'react-loader-spinner';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-bs5';
import Breadcrumb from '../../components/general/breadcrumb';
import IndicatorCard from '../../components/general/indicator-card';
import { AppSettings } from '../../config/app-settings.js';
import useDashboardStore from '../../stores/dashboard-store';
import { useDashboardData } from '../../hooks/useDashboard';

DataTable.use(DT);

const REPORT_OPERATIONS = [
  'annual_average_ci',
  'seasonal_average_ci',
  'monthly_average_ci',
];

// All card colours and icons are owned here — never trusted from the API response
const INDICATOR_THEME = {
  annual: { bgColor: 'bg-primary', icon: 'fa fa-chart-line' },
  spring: { bgColor: 'bg-green', icon: 'fa fa-seedling' },
  summer: { bgStyle: { backgroundColor: '#d4760a' }, icon: 'fa fa-sun' },
  fall: { bgStyle: { backgroundColor: '#b5451b' }, icon: 'fa fa-tree' },
  winter: { bgColor: 'bg-info', icon: 'fa fa-snowflake' },
  twth:    { bgColor: 'bg-teal',   icon: 'fa fa-road' },
  'm-f':   { bgColor: 'bg-blue',   icon: 'fa fa-road' },
  'sat/sun': { bgColor: 'bg-orange', icon: 'fa fa-road' },
};

// Expands abbreviated DOW codes to their full names
const expandDowLabel = (text) => {
  if (!text) return text;
  return text
    .replace(/TWTh/i, 'Tuesday, Wednesday & Thursday')
    .replace(/M-F/i, 'Monday to Friday')
    .replace(/Sat\/Sun/i, 'Saturday & Sunday');
};

const CHART_FONT = "'Open Sans', sans-serif";

const DOW_COLORS = { twth: '#20c997', 'm-f': '#0d6efd', 'sat/sun': '#fd7e14' };
const getBarColor = (title) => {
  const t = title.toLowerCase();
  for (const [key, color] of Object.entries(DOW_COLORS)) {
    if (t.includes(key)) return color;
  }
  return '#6c757d';
};

const SEASONS = [
  { key: 'spring', label: 'Spring' },
  { key: 'summer', label: 'Summer' },
  { key: 'fall',   label: 'Fall'   },
  { key: 'winter', label: 'Winter' },
];

function Dashboard() {
  const context = useContext(AppSettings);
  const currentDate = new Date().toString();

  // ==================== ZUSTAND STATE ====================
  const reportParameters = useDashboardStore((state) => state.reportParameters);
  const hasValidParameters = useDashboardStore((state) => state.hasValidParameters);
  const networkOptions = useDashboardStore((state) => state.networkOptions);
  const datasetOptions = useDashboardStore((state) => state.datasetOptions);
  const timePeriodOptions = useDashboardStore((state) => state.timePeriodOptions);
  const subnetOptions = useDashboardStore((state) => state.subnetOptions);
  const setReportSummary = useDashboardStore((state) => state.setReportSummary);

  // ==================== DATA FETCHING ====================
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useDashboardData(reportParameters, REPORT_OPERATIONS);

  // Extract data from response
  const indicatorsData = dashboardData?.indicator_data || [];
  const datatableData = dashboardData?.datatable_data || [];

  useEffect(() => {
    setReportSummary(dashboardData?.report_summary || null);
  }, [dashboardData]);

  // ==================== LAYOUT SETUP ====================
  useEffect(() => {
    context.handleSetAppContentClass('');
    context.handleSetAppSidebarTwo(false);
    context.handleSetAppSidebarWide(true);
    context.handleSetAppTopMenu(true);

    return () => {
      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppTopMenu(false);
    };
  }, []);

  // ==================== RENDER HELPERS ====================
  const getIndicatorTheme = (title, operationType) => {
    const t = (title || '').toLowerCase();
    for (const [key, theme] of Object.entries(INDICATOR_THEME)) {
      if (t.includes(key)) return theme;
    }
    // Section-level fallback when title doesn't match any keyword
    if (operationType?.includes('annual')) return { bgColor: 'bg-primary',   icon: 'fa fa-road' };
    if (operationType?.includes('seasonal')) return { bgColor: 'bg-teal',    icon: 'fa fa-leaf'       };
    return { bgColor: 'bg-secondary', icon: 'fa fa-chart-bar' };
  };

  const renderLoadingOverlay = () => (
    <div className="loading-overlay">
      <div className="loading-content">
        <Oval
          height={80}
          width={80}
          color="var(--bs-app-theme)"
          wrapperStyle={{}}
          wrapperClass="justify-content-center"
          visible={true}
          ariaLabel="oval-loading"
          secondaryColor="var(--bs-app-theme)"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        <h5 className="mt-3 text-white">Loading Dashboard Data</h5>
      </div>
    </div>
  );

  const renderErrorAlert = () => (
    <div className="alert alert-danger" role="alert">
      <i className="fa fa-exclamation-triangle me-2"></i>
      <strong>Error:</strong> {error?.message || 'Failed to load dashboard data'}
    </div>
  );

  const renderEmptyState = (message = 'Please select controls to load data.') => (
    <div className="text-center w-100 my-3 text-muted">{message}</div>
  );

  const renderIndicatorSection = (type) => (
    <div className="panel panel-inverse" key={type}>
      <div className="panel-heading">
        <h4 className="panel-title">
          {type
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l) => l.toUpperCase())
            .replace(/\bCi\b/g, 'CI*')}
        </h4>

        <div className="mt-2 d-flex flex-wrap gap-2">
          {reportParameters.network && (
            <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
              Network:{" "}
              {networkOptions.find(
                (opt) => opt.value === reportParameters.network
              )?.label || reportParameters.network}
            </span>
          )}
          {reportParameters.dataset && (
            <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
              Dataset:{" "}
              {datasetOptions.find(
                (opt) => opt.value === reportParameters.dataset
              )?.label || reportParameters.dataset}
            </span>
          )}
          {reportParameters.year && (
            <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
              Year: {reportParameters.year}
            </span>
          )}
          {reportParameters.timePeriod && (
            <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
              Time Period:{" "}
              {timePeriodOptions.find(
                (opt) => opt.value === reportParameters.timePeriod
              )?.label || reportParameters.timePeriod}
            </span>
          )}
          {reportParameters.subnet && (
            <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
              Subnet:{" "}
              {subnetOptions.find(
                (opt) => opt.value === reportParameters.subnet
              )?.label || reportParameters.subnet}
            </span>
          )}
        </div>
      </div>
      <div className="row p-3">
        {indicatorsData.length > 0 ? (
          indicatorsData
            .filter(({ indicatorType }) => indicatorType === type)
            .map(({ indicatorType, data }, idx) =>
              data.map((indicator, i) => {
                const theme = getIndicatorTheme(indicator.title, type);
                return (
                  <IndicatorCard
                    title={indicator.title}
                    value={indicator.value || 0}
                    icon={theme.icon}
                    colSize={indicator.colSize || 4}
                    bgColor={theme.bgStyle ? '' : (theme.bgColor || 'bg-secondary')}
                    bgStyle={theme.bgStyle}
                    progress={indicator.progress || ''}
                    description={
                      indicator.description || `${type.replace(/_/g, ' ')} CI`
                    }
                    className="indicator-card"
                    key={`${indicatorType}-${idx}-${i}`}
                  />
                );
              })
            )
        ) : (
          renderEmptyState()
        )}
      </div>
    </div>
  );

  const renderDowBarChart = () => {
    const annualIndicators = indicatorsData
      .filter((d) => d.indicatorType === 'annual_average_ci')
      .flatMap((d) => d.data);
    if (!annualIndicators.length) return renderEmptyState();

    const buildBarOptions = (indicators) => ({
      chart: { type: 'bar', toolbar: { show: false }, fontFamily: CHART_FONT },
      plotOptions: { bar: { borderRadius: 4, distributed: true, dataLabels: { position: 'top' } } },
      dataLabels: { enabled: true, formatter: (v) => v.toFixed(1), offsetY: -20, style: { fontSize: '12px', colors: ['#fff'] } },
      legend: { show: true, fontSize: '11px', itemMargin: { horizontal: 6 }, formatter: (label) => expandDowLabel(label) },
      xaxis: { categories: indicators.map((d) => d.title), labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } },
      yaxis: { title: { text: 'Average Congestion Index (×100)' }, labels: { formatter: (v) => v.toFixed(1) } },
      colors: indicators.map((d) => getBarColor(d.title)),
      tooltip: { y: { formatter: (v) => v.toFixed(2) } },
    });

    const buildSeries = (indicators) => [{
      name: 'Average Congestion Index',
      data: indicators.map((d) => parseFloat((d.value * 100).toFixed(2))),
    }];

    return <ReactApexChart type="bar" options={buildBarOptions(annualIndicators)} series={buildSeries(annualIndicators)} height={300} />;
  };

  const renderSeasonalDowCharts = () => {
    const seasonalIndicators = indicatorsData
      .filter((d) => d.indicatorType === 'seasonal_average_ci')
      .flatMap((d) => d.data);
    if (!seasonalIndicators.length) return null;

    const seasonCharts = SEASONS.map(({ key, label }) => {
      const seasonData = seasonalIndicators.filter((d) => d.title.toLowerCase().includes(key));
      if (!seasonData.length) return null;

      const options = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: CHART_FONT },
        plotOptions: { bar: { borderRadius: 4, distributed: true, dataLabels: { position: 'top' } } },
        dataLabels: { enabled: true, formatter: (v) => v.toFixed(1), offsetY: -20, style: { fontSize: '12px', colors: ['#fff'] } },
        legend: { show: true, fontSize: '11px', itemMargin: { horizontal: 6 }, formatter: (label) => expandDowLabel(label) },
        xaxis: { categories: seasonData.map((d) => d.title), labels: { show: false } },
        yaxis: { title: { text: 'Average Congestion Index (×100)' }, labels: { formatter: (v) => v.toFixed(1) } },
        colors: seasonData.map((d) => getBarColor(d.title)),
        tooltip: { y: { formatter: (v) => v.toFixed(2) } },
      };
      const series = [{ name: 'Average Congestion Index', data: seasonData.map((d) => parseFloat((d.value * 100).toFixed(2))) }];

      return (
        <div className="col-xl-6 col-md-12 mb-4 px-4" key={key}>
          <h6 className="fw-bold mb-2">{label}</h6>
          <ReactApexChart type="bar" options={options} series={series} height={320} />
        </div>
      );
    }).filter(Boolean);

    return (
      <div className="row">
        <div className="col-xl-12 col-md-12">
          <div className="panel panel-inverse">
            <div
              className="panel-heading"
              data-bs-toggle="collapse"
              data-bs-target="#seasonalDowCollapse"
              style={{ cursor: 'pointer' }}
            >
              <h4 className="panel-title">
                <i className="fa fa-bar-chart me-2"></i> Seasonal Congestion Index Graphs
                <i className="fa fa-chevron-down float-end"></i>
              </h4>
            </div>
            <div id="seasonalDowCollapse" className="collapse">
              <div className="panel-body">
                <div className="row">{seasonCharts}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMonthlyLineChart = () => {
    if (!datatableData?.length) return renderEmptyState('No monthly data available.');
    const tableConfig = datatableData[0];
    if (!tableConfig?.data?.length) return renderEmptyState('No monthly data available.');

    const categories = tableConfig.data.map((row) => row.monthName);
    const series = tableConfig.columns
      .filter((col) => col.data !== 'monthName')
      .map((col) => ({
        name: expandDowLabel(col.title),
        data: tableConfig.data.map((row) => parseFloat((parseFloat(row[col.data]) * 100).toFixed(2))),
      }));

    const options = {
      chart: { type: 'line', toolbar: { show: false }, fontFamily: CHART_FONT },
      stroke: { curve: 'smooth', width: 2 },
      markers: { size: 5, strokeWidth: 2, hover: { size: 7 } },
      xaxis: { categories },
      yaxis: { title: { text: 'Congestion Index (×100)' }, labels: { formatter: (v) => v.toFixed(1) } },
      legend: { position: 'top' },
      tooltip: { y: { formatter: (v) => v.toFixed(2) } },
    };

    return <ReactApexChart type="line" options={options} series={series} height={350} />;
  };

  const renderDataTable = () => {
    if (!datatableData || datatableData.length === 0) {
      return renderEmptyState('Please select controls to load monthly CI data.');
    }

    const tableConfig = datatableData[0];

    return (
      <DataTable
        data={tableConfig.data}
        options={{
          responsive: true,
          dom: 'Bfrtip',
          order: [[0, 'asc']],
          searching: false,
          paging: false,
          info: false,
          columnDefs: [
            {
              targets: 0,
              title: '#',
              data: null,
              render: (data, type, row, meta) => meta.row + 1,
              className: 'text-center',
            },
            ...tableConfig.columns.map((header, index) => {
              const columnDef = {
                targets: index + 1,
                title: header.title,
                data: header.data,
                className: 'text-center',
              };

              // Format numeric columns (skip month names)
              if (header.data !== 'monthName') {
                columnDef.render = (data, type) => {
                  if (type === 'sort' || type === 'filter') {
                    return parseFloat(data) * 100;
                  }
                  const value = parseFloat(data) * 100;
                  return isNaN(value) ? '0.0' : value.toFixed(1);
                };
              }

              return columnDef;
            }),
          ],
        }}
        className="table table-striped table-bordered"
      />
    );
  };

  // ==================== RENDER ====================
  return (
    <>
      {/* Loading Overlay */}
      {isLoading && renderLoadingOverlay()}

      {/* Breadcrumb */}
      <div className="row">
        <div className="col-sm-12">
          <Breadcrumb />
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="row">
        <div className="col-sm-12">
          <h1 className="page-header mb-3 fw-bold" style={{ fontSize: '2rem', letterSpacing: '0.03em' }}>ANNUAL CONGESTION INDEX DASHBOARD</h1>
          <div className="d-sm-flex align-items-center mb-3">
            <div className="text-gray-600 fw-bold mt-2 mt-sm-0">
              <span>{currentDate}</span><br />
              <span className='fw-bolder'>* Annual and Seasonal Average congestion indices (CI) may not contain data for all months--interpret with caution</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row">
          <div className="col-sm-12">{renderErrorAlert()}</div>
        </div>
      )}

      {/* Annual Average CI Indicators */}
      <div className="row">
        <div className="col-xl-12 col-md-12">
          {renderIndicatorSection('annual_average_ci')}
        </div>
      </div>

      {/* Annual DOW Bar Chart */}
      {indicatorsData.length > 0 && (
        <div className="row">
          <div className="col-xl-12 col-md-12">
            <div className="panel panel-inverse">
              <div
                className="panel-heading"
                data-bs-toggle="collapse"
                data-bs-target="#dowChartCollapse"
                style={{ cursor: 'pointer' }}
              >
                <h4 className="panel-title">
                  <i className="fa fa-bar-chart me-2"></i> Annual Congestion Index Graphs
                  <i className="fa fa-chevron-down float-end"></i>
                </h4>
              </div>
              <div id="dowChartCollapse" className="collapse">
                <div className="panel-body">{renderDowBarChart()}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seasonal Average CI Indicators */}
      <div className="row">
        <div className="col-xl-12 col-md-12">
          {renderIndicatorSection('seasonal_average_ci')}
        </div>
      </div>

      {/* Seasonal DOW Bar Charts — one per season */}
      {indicatorsData.length > 0 && renderSeasonalDowCharts()}

      {/* Monthly Average CI Data Table */}
      <div className="row">
        <div className="col-xl-12 col-md-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">Monthly Average CI</h4>
              <div className="mt-2 d-flex flex-wrap gap-2">
                {reportParameters.network && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Network:{" "}
                    {networkOptions.find(
                      (opt) => opt.value === reportParameters.network
                    )?.label || reportParameters.network}
                  </span>
                )}
                {reportParameters.dataset && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Dataset:{" "}
                    {datasetOptions.find(
                      (opt) => opt.value === reportParameters.dataset
                    )?.label || reportParameters.dataset}
                  </span>
                )}
                {reportParameters.year && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Year: {reportParameters.year}
                  </span>
                )}
                {reportParameters.timePeriod && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Time Period:{" "}
                    {timePeriodOptions.find(
                      (opt) => opt.value === reportParameters.timePeriod
                    )?.label || reportParameters.timePeriod}
                  </span>
                )}
                {reportParameters.subnet && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Subnet:{" "}
                    {subnetOptions.find(
                      (opt) => opt.value === reportParameters.subnet
                    )?.label || reportParameters.subnet}
                  </span>
                )}
              </div>
            </div>
            <div className="panel-body">{renderDataTable()}</div>
          </div>
        </div>
      </div>

      {/* Monthly Line Chart Accordion */}
      {datatableData.length > 0 && (
        <div className="row">
          <div className="col-xl-12 col-md-12">
            <div className="panel panel-inverse">
              <div
                className="panel-heading"
                data-bs-toggle="collapse"
                data-bs-target="#monthlyChartCollapse"
                style={{ cursor: 'pointer' }}
              >
                <h4 className="panel-title">
                  <i className="fa fa-line-chart me-2"></i> Monthly Congestion Index Trend
                  <i className="fa fa-chevron-down float-end"></i>
                </h4>
              </div>
              <div id="monthlyChartCollapse" className="collapse">
                <div className="panel-body">{renderMonthlyLineChart()}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Dashboard;